import { TASK_PRIORITIES, TASK_PRIORITY_LABEL, type TaskPriority } from '@supply/contracts'
import * as React from 'react'

import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Area, Member } from '@/data/types'
import { useSession } from '@/lib/session'

/** Valor do executor: pessoa do sistema OU nome livre (terceiro, fornecedor). */
export interface ExecutorValue {
  executor_id: string | null
  executor_text: string | null
}

const FREE = '__livre'
const NONE = '__nenhum'

/**
 * Seletor de executor. Pessoas da área primeiro (quem normalmente executa),
 * depois as demais; "Outro (nome livre)" para quem não tem acesso ao sistema.
 */
export function ExecutorField({
  id,
  value,
  onChange,
  areaId,
  disabled,
}: {
  id?: string
  value: ExecutorValue
  onChange: (v: ExecutorValue) => void
  areaId: string | null
  disabled?: boolean
}) {
  const { ref } = useSession()
  const active = ref.members.filter((m) => m.is_active)
  const inArea = new Set(ref.areaMembers.filter((am) => am.area_id === areaId).map((am) => am.member_id))
  const fromArea = active.filter((m) => inArea.has(m.id))
  // Executor só executa tarefa da própria área (`supply.task_executor_same_area`);
  // de fora, só gerente ou gestor.
  const leads = new Set([
    ...ref.areaMembers.filter((am) => am.role === 'responsavel').map((am) => am.member_id),
    ...ref.roles.map((r) => r.member_id),
  ])
  const others = active.filter((m) => !inArea.has(m.id) && leads.has(m.id))
  const [free, setFree] = React.useState(!value.executor_id && !!value.executor_text)

  const selectValue = value.executor_id ?? (free ? FREE : NONE)

  return (
    <div className="space-y-2">
      <Select
        value={selectValue}
        disabled={disabled}
        onValueChange={(v) => {
          if (v === FREE) {
            setFree(true)
            onChange({ executor_id: null, executor_text: value.executor_text ?? '' })
          } else if (v === NONE) {
            setFree(false)
            onChange({ executor_id: null, executor_text: null })
          } else {
            setFree(false)
            onChange({ executor_id: v, executor_text: null })
          }
        }}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder="Quem vai executar" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Sem executor</SelectItem>
          {fromArea.length > 0 && (
            <SelectGroup>
              <SelectLabel>Da área</SelectLabel>
              {fromArea.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.full_name}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          {others.length > 0 && (
            <SelectGroup>
              <SelectLabel>Gerentes e gestores</SelectLabel>
              {others.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.full_name}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          <SelectSeparator />
          <SelectItem value={FREE}>Outro (nome livre)</SelectItem>
        </SelectContent>
      </Select>
      {selectValue === FREE && (
        <Input
          aria-label="Nome do executor"
          typingAssist={false}
          placeholder="Nome de quem executa"
          value={value.executor_text ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ executor_id: null, executor_text: e.target.value })}
        />
      )}
    </div>
  )
}

export function AreaSelect({
  id,
  value,
  onChange,
  areas,
  disabled,
  placeholder = 'Selecione a área',
}: {
  id?: string
  value: string | null
  onChange: (id: string) => void
  areas: readonly Area[]
  disabled?: boolean
  placeholder?: string
}) {
  return (
    <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {areas.map((a) => (
          <SelectItem key={a.id} value={a.id}>
            {a.code} — {a.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function PrioritySelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id?: string
  value: TaskPriority
  onChange: (p: TaskPriority) => void
  disabled?: boolean
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TaskPriority)} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TASK_PRIORITIES.map((p) => (
          <SelectItem key={p} value={p}>
            {TASK_PRIORITY_LABEL[p]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function MemberSelect({
  id,
  value,
  onChange,
  members,
  disabled,
  placeholder = 'Selecione',
  allowNone = true,
}: {
  id?: string
  value: string | null
  onChange: (id: string | null) => void
  members: readonly Member[]
  disabled?: boolean
  placeholder?: string
  allowNone?: boolean
}) {
  return (
    <Select value={value ?? NONE} onValueChange={(v) => onChange(v === NONE ? null : v)} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>Ninguém</SelectItem>}
        {members.map((m) => (
          <SelectItem key={m.id} value={m.id}>
            {m.full_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Áreas onde a pessoa pode lançar (gestor: todas as ativas). */
export function useWritableAreas(): Area[] {
  const { activeAreas, isGestor, me } = useSession()
  return React.useMemo(
    () => (isGestor ? [...activeAreas] : activeAreas.filter((a) => me.areas.some((x) => x.areaId === a.id))),
    [activeAreas, isGestor, me],
  )
}
